import type { ChainIcon } from "@src/components/DefuseSDK/constants/blockchains"

export type ChainId = "near" | "evm" | "solana" | "stellar"

export type ConnectorResolver =
  | { kind: "wagmi-rdns"; rdns: string }
  | { kind: "wagmi-id"; connectorId: string }
  | { kind: "wagmi-any"; tries: readonly string[] }
  | { kind: "solana-adapter"; adapterName: string }
  | { kind: "near-walletid"; walletId: string }
  | { kind: "stellar-walletid"; walletId: string }

export interface WalletEntry {
  id: string
  name: string
  icon?: ChainIcon
  chains: readonly ChainId[]
  resolve: Partial<Record<ChainId, ConnectorResolver>>
}

export const WALLETS: readonly WalletEntry[] = [
  {
    id: "metamask",
    name: "MetaMask",
    chains: ["evm"],
    icon: {
      dark: "/static/icons/wallets/meta-mask.svg",
      light: "/static/icons/wallets/meta-mask.svg",
    },
    resolve: { evm: { kind: "wagmi-rdns", rdns: "io.metamask" } },
  },
  {
    id: "coinbase",
    name: "Coinbase Wallet",
    chains: ["evm"],
    icon: {
      dark: "/static/icons/wallets/coinbase-wallet-light.svg",
      light: "/static/icons/wallets/coinbase-wallet.svg",
    },
    resolve: { evm: { kind: "wagmi-id", connectorId: "coinbaseWalletSDK" } },
  },
  {
    id: "rabby",
    name: "Rabby Wallet",
    chains: ["evm"],
    icon: {
      dark: "/static/icons/wallets/rabby.svg",
      light: "/static/icons/wallets/rabby.svg",
    },
    resolve: { evm: { kind: "wagmi-rdns", rdns: "io.rabby" } },
  },
  {
    id: "binance-w3w",
    name: "Binance Web3",
    chains: ["evm"],
    icon: {
      dark: "/static/icons/wallets/binance-w3w.svg",
      light: "/static/icons/wallets/binance-w3w.svg",
    },
    resolve: { evm: { kind: "wagmi-id", connectorId: "com.binance.w3w" } },
  },
  {
    id: "walletconnect",
    name: "WalletConnect",
    chains: ["evm"],
    icon: {
      dark: "/static/icons/wallets/wallet-connect.svg",
      light: "/static/icons/wallets/wallet-connect.svg",
    },
    resolve: { evm: { kind: "wagmi-id", connectorId: "walletConnect" } },
  },
  {
    id: "phantom",
    name: "Phantom",
    chains: ["evm", "solana"],
    resolve: {
      evm: { kind: "wagmi-rdns", rdns: "app.phantom" },
      solana: { kind: "solana-adapter", adapterName: "Phantom" },
    },
  },
  {
    id: "solflare",
    name: "Solflare",
    chains: ["solana"],
    resolve: {
      solana: { kind: "solana-adapter", adapterName: "Solflare" },
    },
  },
  {
    id: "hot",
    name: "Hot Wallet",
    icon: {
      dark: "/static/icons/wallets/hot-wallet.svg",
      light: "/static/icons/wallets/hot-wallet.svg",
    },
    chains: ["near", "evm", "solana", "stellar"],
    resolve: {
      near: { kind: "near-walletid", walletId: "hot-wallet" },
      // Prefer Hot's installed extension (EIP-6963), fall back to WalletConnect
      // for mobile pairing. The exact rdns is best-effort — the modal also
      // dedupes by display name as a safety net.
      evm: {
        kind: "wagmi-any",
        tries: ["org.hot-labs.wallet", "walletConnect"],
      },
      solana: { kind: "solana-adapter", adapterName: "WalletConnect" },
      stellar: { kind: "stellar-walletid", walletId: "hot-wallet" },
    },
  },
  {
    id: "meteor-wallet",
    name: "Meteor Wallet",
    icon: {
      dark: "/static/icons/wallets/meteor-wallet-light.svg",
      light: "/static/icons/wallets/meteor-wallet.svg",
    },
    chains: ["near"],
    resolve: { near: { kind: "near-walletid", walletId: "meteor-wallet" } },
  },
  {
    id: "intear-wallet",
    name: "Intear Wallet",
    icon: {
      dark: "/static/icons/wallets/intear-wallet-light.svg",
      light: "/static/icons/wallets/intear-wallet.svg",
    },
    chains: ["near"],
    resolve: { near: { kind: "near-walletid", walletId: "intear-wallet" } },
  },
  {
    id: "ledger",
    name: "Ledger",
    icon: {
      dark: "/static/icons/wallets/ledger-light.svg",
      light: "/static/icons/wallets/ledger.svg",
    },
    chains: ["near"],
    resolve: { near: { kind: "near-walletid", walletId: "ledger" } },
  },
  {
    id: "mynearwallet",
    name: "MyNearWallet",
    icon: {
      dark: "/static/icons/wallets/my-near-wallet.svg",
      light: "/static/icons/wallets/my-near-wallet.svg",
    },
    chains: ["near"],
    resolve: { near: { kind: "near-walletid", walletId: "mynearwallet" } },
  },
  {
    id: "freighter",
    name: "Freighter",
    chains: ["stellar"],
    resolve: { stellar: { kind: "stellar-walletid", walletId: "freighter" } },
  },
  {
    id: "lobstr",
    name: "Lobstr",
    chains: ["stellar"],
    resolve: { stellar: { kind: "stellar-walletid", walletId: "lobstr" } },
  },
  {
    id: "xbull",
    name: "xBull",
    chains: ["stellar"],
    resolve: { stellar: { kind: "stellar-walletid", walletId: "xbull" } },
  },
]

export const walletsForChain = (chain: ChainId): readonly WalletEntry[] =>
  WALLETS.filter((w) => w.chains.includes(chain))

export const findWalletById = (id: string): WalletEntry | undefined =>
  WALLETS.find((w) => w.id === id)

export function connectorIdsForResolver(
  resolver: ConnectorResolver | undefined
): readonly string[] {
  if (!resolver) return []
  switch (resolver.kind) {
    case "wagmi-rdns":
      return [resolver.rdns]
    case "wagmi-id":
      return [resolver.connectorId]
    case "wagmi-any":
      return resolver.tries
    case "solana-adapter":
      return [resolver.adapterName]
    case "near-walletid":
    case "stellar-walletid":
      return [resolver.walletId]
  }
}

export function connectorIdsForWallet(
  wallet: WalletEntry,
  chain: ChainId
): readonly string[] {
  return connectorIdsForResolver(wallet.resolve[chain])
}

export function findWalletByConnector({
  chain,
  id,
  name,
}: {
  chain: ChainId
  id?: string
  name?: string
}): WalletEntry | undefined {
  const resolverMatch = id
    ? WALLETS.find((wallet) =>
        connectorIdsForWallet(wallet, chain).includes(id)
      )
    : undefined
  if (resolverMatch) return resolverMatch

  const normalizedName = name?.toLowerCase()
  if (!normalizedName) return undefined

  return WALLETS.find((wallet) => wallet.name.toLowerCase() === normalizedName)
}

export function walletIconForConnector(params: {
  chain: ChainId
  id?: string
  name?: string
}): ChainIcon | undefined {
  return findWalletByConnector(params)?.icon
}

/**
 * For a registry entry's resolver on a given chain, return the wagmi
 * connector ids that should be deduped against auto-discovered connectors.
 * Used by the modal merge logic to avoid showing the same wallet twice.
 */
export function evmConnectorIdsForResolver(
  resolver: ConnectorResolver | undefined
): readonly string[] {
  return connectorIdsForResolver(resolver)
}

/**
 * Wagmi connector ids that don't represent an installable extension —
 * they always work via popup or QR pairing. Used to mark them as
 * "available" rather than "installed" in the picker.
 */
export const ALWAYS_AVAILABLE_EVM_CONNECTOR_IDS: readonly string[] = [
  "walletConnect",
  "coinbaseWalletSDK",
]
